import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { LocationModel } from 'projects/core/src/lib/models/location.model';
import { RoomModel } from 'projects/core/src/lib/models/room.model';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';
import { Subscription } from 'rxjs';
import { SelectedSuggestion } from '../move-order-suggestions/move-order-suggestions.types';

export interface MoveDataPayload {
  date: Date;
  location: LocationModel;
}

enum FormMode {
  Onsite = 'onsite',
  Virtual = 'virtual',
  Norooms = 'norooms',
}

@Component({
  selector: 'app-move-order-data',
  templateUrl: './move-order-data.component.html',
  styleUrls: ['./move-order-data.component.scss']
})
export class MoveOrderDataComponent implements OnInit, OnDestroy {

  @Input() form: UntypedFormGroup = null;
  @Input() order: AcModel = null;
  @Input() options: { [key: string]: Array<any> };
  @Input() locations: Array<LocationModel> = [];

  @Output() changed = new EventEmitter<MoveDataPayload>();

  public dateActive = false;
  public locationActive = false;

  private subscriptions: Array<Subscription> = [];

  public country: string | null = null;
  public timezone: string | null = null;
  public subForm: UntypedFormGroup = this.fb.group({
    date: new UntypedFormControl(null),
    location: new UntypedFormControl(null),
    country: new UntypedFormControl(null),
    room1: new UntypedFormControl(''),
    room2: new UntypedFormControl(''),
    room_main: new UntypedFormControl(''),
    room_preparation: new UntypedFormControl(''),
    conference_url: new UntypedFormControl('', [CustomValidators.url()]),
    conference_info: new UntypedFormControl(''),
    prebriefing_url: new UntypedFormControl('', [CustomValidators.url()]),
    prebriefing_info: new UntypedFormControl(''),
    tz: this.fb.group({}),
  });
  public locationDetailsVisible = false;
  public formMode: FormMode | null = null;
  public FormModes = FormMode;
  public roomsMain: Array<RoomModel> = [];
  public roomsPreparation: Array<RoomModel> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private helper: HelperService,
  ) { }

  ngOnInit(): void {
    this.initChangeListeners();
    this.initDate();
    this.initLocation();
    this.setFields();
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  public hasPrebriefing(): boolean {
    return this.order?.prebriefing === true || this.order?.template?.settings?.prebriefing === true;
  }

  public getDstLabel(): string {
    return ' (' + this.helper.getTimezoneName(this.order?.timezone, this.subForm.get('start_time')?.value) + ')';
  }

  public dateActiveChanged(): void {
    this.setFields();
  }

  public locationActiveChanged(): void {
    this.setFields();
  }

  public getEndTime(): Date | null {
    const value = this.subForm.get('date').value;
    if (this.order && value !== null) {
      return moment(value).add(this.order.duration, 'minutes').toDate();
    }
    return null;
  }

  public getPayload(): any {
    if (this.subForm.valid) {
      return {
        date_changed: this.dateActive,
        location_changed: this.locationActive,
        values: {
          date: (this.subForm.get('date').value) ? moment(this.subForm.get('date').value).format('YYYY-MM-DD HH:mm:ss') : null,
          location: this.subForm.get('location').value?.id || null,
          room1: this.subForm.get('room1').value?.id || null,
          room2: this.subForm.get('room2').value?.id || null,
          room_main: this.subForm.get('room_main').value || null,
          room_preparation: this.subForm.get('room_preparation').value || null,
          conference_url: this.subForm.get('conference_url').value || null,
          conference_info: this.subForm.get('conference_info').value || null,
          prebriefing_url: this.subForm.get('prebriefing_url').value || null,
          prebriefing_info: this.subForm.get('prebriefing_info').value || null,
        }
      };
    } else {
      FormHelper.markFormAsDirty(this.subForm);
      return null;
    }
  }

  public setSuggestion(suggestion: SelectedSuggestion): void {
    // set date
    const suggestionDate = moment(suggestion.date);
    const orderDate = moment(this.order.start_time_admin);
    if (!suggestionDate.isSame(orderDate, 'day')) {
      orderDate.set({
        year: suggestionDate.year(),
        month: suggestionDate.month(),
        date: suggestionDate.date()
      });
      this.subForm.get('date').setValue(orderDate.toDate());
      this.dateActive = true;
    } else {
      this.dateActive = false;
    }
    // set location
    if (suggestion.location?.id !== this.subForm.get('location').value?.id) {
      this.subForm.get('location').setValue(suggestion.location);
    }
    this.locationActive = this.subForm.get('location').value?.id !== this.order.location_id;
    // set rooms
    if (suggestion.room1?.id) {
      this.subForm.get('room1').setValue(suggestion.room1);
    }
    if (suggestion.room2?.id) {
      this.subForm.get('room2').setValue(suggestion.room2);
    }
    this.setFields();
  }

  private initDate(): void {
    if (this.order) {
      const date = this.order.start_time_admin ? moment(this.order.start_time_admin).toDate() : null;
      this.subForm.get('date').setValue(date);
    }
  }

  private initLocation(): void {
    if (this.order) {
      // location
      const location = this.locations?.find((location: LocationModel) => {
        return location.id === this.order.location_id;
      }) || null;
      this.subForm.get('location').setValue(location);
      // other fields
      this.subForm.patchValue({
        room1: this.getOrderRoom('main') || '',
        room2: this.getOrderRoom('preparation') || '',
        conference_url: this.order.conference_url,
        conference_info: this.order.conference_info,
        prebriefing_url: this.order.prebriefing_url,
        prebriefing_info: this.order.prebriefing_info,
      });
      this.country = this.order.country;
      this.timezone = this.order.timezone;
    }
  }

  private setFields(): void {
    if (this.dateActive) {
      this.subForm.get('date').enable();
    } else {
      this.subForm.get('date').disable();
      this.initDate();
    }
    if (this.locationActive) {
      this.subForm.get('location').enable();
      this.locationDetailsVisible = true;
    } else {
      this.subForm.get('location').disable();
      this.locationDetailsVisible = false;
      this.clearValidators();
      this.initLocation();
    }
    this.emitData();
  }

  private initChangeListeners(): void {
    this.subscriptions.push(this.subForm.get('date').valueChanges.subscribe(() => {
      this.setRooms();
      this.emitData();
    }));
    this.subscriptions.push(this.subForm.get('location').valueChanges.subscribe(() => {
      this.setFormMode();
      this.setRooms();
      this.emitData();
    }));
  }

  private emitData(): void {
    this.changed.emit({
      date: (this.dateActive) ? this.subForm.get('date').value : null,
      location: (this.locationActive) ? this.subForm.get('location').value : null,
    });
  }

  private setFormMode(): void {
    this.clearValidators();
    const location: LocationModel = this.subForm.get('location').value || null;
    if (location !== null && this.locationActive === true) {
      if (location.virtual === true) {
        this.formMode = FormMode.Virtual;
        this.subForm.get('conference_url').addValidators([Validators.required]);
        this.subForm.get('conference_info').addValidators([Validators.required]);
        if (this.hasPrebriefing() === true) {
          this.subForm.get('prebriefing_url').addValidators([Validators.required]);
          this.subForm.get('prebriefing_info').addValidators([Validators.required]);
        }
      } else if (location.room_count > 0) {
        this.formMode = FormMode.Onsite;
        this.subForm.get('room1').addValidators([Validators.required]);
        this.subForm.get('room2').addValidators([Validators.required]);
      } else {
        this.formMode = FormMode.Norooms;
        this.subForm.get('room_main').addValidators([Validators.required]);
        this.subForm.get('room_preparation').addValidators([Validators.required]);
      }
    } else {
      this.formMode = null;
    }
    this.updateValueAndValidity();
  }

  private clearValidators(): void {
    this.subForm.get('room1').clearValidators();
    this.subForm.get('room2').clearValidators();
    this.subForm.get('room_main').clearValidators();
    this.subForm.get('room_preparation').clearValidators();
    this.subForm.get('conference_url').clearValidators();
    this.subForm.get('conference_info').clearValidators();
    this.subForm.get('prebriefing_url').clearValidators();
    this.subForm.get('prebriefing_info').clearValidators();
    this.updateValueAndValidity();
  }

  private updateValueAndValidity(): void {
    this.subForm.get('room1').updateValueAndValidity();
    this.subForm.get('room2').updateValueAndValidity();
    this.subForm.get('room_main').updateValueAndValidity();
    this.subForm.get('room_preparation').updateValueAndValidity();
    this.subForm.get('conference_url').updateValueAndValidity();
    this.subForm.get('conference_info').updateValueAndValidity();
    this.subForm.get('prebriefing_url').updateValueAndValidity();
    this.subForm.get('prebriefing_info').updateValueAndValidity();
  }

  private setRooms(): void {
    // set options
    this.roomsMain = [];
    this.roomsPreparation = [];
    const location: LocationModel = this.subForm.get('location').value || null;
    if (location !== null) {
      this.roomsMain = this.getRoomValues(location, 'main');
      this.roomsPreparation = this.getRoomValues(location, 'preparation');
      // reset values if not in lists
      if (!this.roomsMain.some((room: RoomModel) => room.id === this.subForm.get('room1').value?.id)) {
        this.subForm.get('room1').setValue('');
      }
      if (!this.roomsPreparation.some((room: RoomModel) => room.id === this.subForm.get('room2').value?.id)) {
        this.subForm.get('room2').setValue('');
      }
    }
    // set values
    if (!this.isDateChanged() && !this.isLocationChanged()) {
      this.subForm.get('room1').setValue('');
      this.subForm.get('room2').setValue('');
      this.subForm.get('room_main').setValue('');
      this.subForm.get('room_preparation').setValue('');
      if (this.formMode === FormMode.Onsite) {
        this.subForm.get('room1').setValue(this.getOrderRoom('main') || '');
        this.subForm.get('room2').setValue(this.getOrderRoom('preparation') || '');
      } else if (this.formMode === FormMode.Norooms) {
        this.subForm.get('room_main').setValue(this.order?.location_other?.room_main || '');
        this.subForm.get('room_preparation').setValue(this.order?.location_other?.room_preparation || '');
      }
    } else {
      if (
        this.subForm.get('room1').value === '' &&
        this.subForm.get('room2').value === '' &&
        this.roomsMain.length === 1 &&
        this.roomsPreparation.length === 1
      ) {
        this.subForm.get('room1').setValue(this.roomsMain[0]);
        this.subForm.get('room2').setValue(this.roomsPreparation[0]);
      }
    }
  }

  private getRoomValues(location: LocationModel, type: string): Array<RoomModel> {
    return location.rooms.filter((room: RoomModel) => {
      const selectedDate = moment(this.subForm.get('date').value);
      const orderStartDate = moment(this.order.start_time_admin);
      return room.type === type && !room.blocked_days.some((day: string) => {
        const dayDate = moment(day);
        return dayDate.isSame(selectedDate, 'day') && !dayDate.isSame(orderStartDate, 'day');
      });
    });
  }

  private getOrderRoom(type: string): RoomModel | null {
    const location = this.locations?.find((location: LocationModel) => {
      return location.id === this.order.location_id;
    }) || null;
    if (location !== null) {
      return location.rooms.find((room: RoomModel) => {
        const orderRoom = (type === 'preparation') ? this.order.getPreparationRoom() : this.order.getMainRoom();
        return room.id === orderRoom?.id && room.type === type;
      }) || null;
    }
    return null;
  }

  private isDateChanged(): boolean {
    const orderDate = moment(this.order?.start_time_admin);
    const selectedDate = moment(this.subForm.get('date').value);
    return orderDate.isSame(selectedDate, 'hour') === false;
  }

  private isLocationChanged(): boolean {
    const orderLocation = this.order?.location_id;
    const selectedLocation = this.subForm.get('location').value?.id;
    return orderLocation !== selectedLocation;
  }

}
